// Calendar-date helpers for Lists / Calendar. Dates are 'YYYY-MM-DD' strings;
// every Date is anchored at local noon so DST never shifts a day, and
// formatting reads local Y/M/D rather than toISOString (which is UTC).

export const BUSINESS_TZ = 'America/Toronto';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function businessToday(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

export function parseDay(day: string): Date {
  return new Date(`${day}T12:00:00`);
}

export function formatDay(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function addDays(day: string, n: number): string {
  const d = parseDay(day);
  d.setDate(d.getDate() + n);
  return formatDay(d);
}

export function addMonths(day: string, n: number): string {
  const d = parseDay(day);
  d.setDate(1);
  d.setMonth(d.getMonth() + n);
  return formatDay(d);
}

/** ISO weekday: 1 = Monday ... 7 = Sunday. */
export function isoWeekday(day: string): number {
  return parseDay(day).getDay() || 7;
}

export function mondayOf(day: string): string {
  return addDays(day, 1 - isoWeekday(day));
}

export function daysBetween(from: string, to: string): number {
  return Math.round((parseDay(to).getTime() - parseDay(from).getTime()) / 86400000);
}

/** 'Oct 12' */
export function shortDate(day: string): string {
  const d = parseDay(day);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/** 'Thu, Oct 12' */
export function weekdayDate(day: string): string {
  const d = parseDay(day);
  return `${DAYS[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/** 'Thursday, October 12' */
export function longDate(day: string): string {
  const d = parseDay(day);
  return `${DAYS_FULL[d.getDay()]}, ${MONTHS_FULL[d.getMonth()]} ${d.getDate()}`;
}

export function monthTitle(day: string): string {
  const d = parseDay(day);
  return `${MONTHS_FULL[d.getMonth()]} ${d.getFullYear()}`;
}

export function weekdayShort(day: string): string {
  return DAYS[parseDay(day).getDay()];
}

/** Relative wording for chips: 'Today', 'Tomorrow', 'Yesterday', 'Thu, Oct 12'. */
export function relativeDay(day: string, today: string): string {
  const diff = daysBetween(today, day);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  if (diff > 1 && diff < 7) return DAYS_FULL[parseDay(day).getDay()];
  return shortDate(day);
}

/** Toronto calendar day of an ISO timestamp. */
export function dayOfTimestamp(ts: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(ts));
}

export function timeOfTimestamp(ts: string): string {
  return new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}
